import FadeIn from "@/components/FadeIn";
import Parallax from "@/components/Parallax";
import { DotWaterhole, DotUShape } from "@/components/AboriginalPatterns";
import { Users, X } from "lucide-react";

const SectionBadge = ({ icon: Icon, label, color = "primary" }: { icon: any; label: string; color?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`p-2.5 rounded-2xl bg-${color}/8 border border-${color}/15`}>
      <Icon size={16} className={`text-${color}`} />
    </div>
    <span className={`text-[10px] font-semibold uppercase tracking-[0.3em] text-${color}`}>{label}</span>
  </div>
);

const AudienceCard = ({ title, description }: { title: string; description: string }) => (
  <div className="glass-card rounded-xl p-6">
    <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground leading-relaxed">{description}</p>
  </div>
);

const AudienceSection = () => (
  <section className="relative section-padding overflow-hidden">
    <Parallax className="absolute top-1/2 -translate-y-1/2 -right-20" offset={-45} offsetX={25}>
      <DotWaterhole className="text-primary opacity-8" size={420} />
    </Parallax>
    <Parallax className="absolute top-16 left-10" offset={25}>
      <DotUShape className="text-ochre opacity-15" size={70} />
    </Parallax>
    <div className="relative max-w-3xl mx-auto px-6">
      {/* Who This Is For */}
      <FadeIn>
        <SectionBadge icon={Users} label="Who This Is For" color="ochre" />
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="grid gap-4 mb-16">
          <AudienceCard
            title="Private Equity and Corporate Development Teams"
            description="What does the leadership layer look like inside the company you are acquiring? Culture is not a soft variable. It is a valuation variable. Execution risk can be measured and addressed before you close."
          />
          <AudienceCard
            title="Boards Overseeing Acquisitions"
            description="Approving an acquisition is not the same as having confidence the target can execute through integration. Independent clarity on leadership durability protects deal value."
          />
          <AudienceCard
            title="CEOs of Target Companies Preparing for Scrutiny"
            description="Your leadership layer will be examined. The question is whether you surface the weak points yourself, while the cost of fixing them is still low."
          />
        </div>
      </FadeIn>

      {/* Not For */}
      <FadeIn delay={0.2}>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 rounded-2xl bg-muted border border-border/30">
            <X size={16} className="text-muted-foreground" />
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">Not For</span>
        </div>
        <div className="space-y-4 text-muted-foreground leading-relaxed text-lg">
          <p>This work is not for:</p>
          <ul className="space-y-2 pl-1">
            {[
              "HR-led engagement initiatives",
              "Generic culture programs",
              "Organizations already in visible crisis seeking quick fixes",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-muted-foreground/70">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-muted-foreground/30 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p>This is upstream and preventive.</p>
          <p className="text-muted-foreground/60">
            If the fractures are already visible, the intervention required is fundamentally different.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>
);

export default AudienceSection;
